import { z } from 'zod'

/**
 * 시식 기록 (2026-10-25). 공장을 고른 뒤 받은 제품을 맛보고 남긴다 (기획안 D02 시식 평가).
 * 출처: 공장 샘플 · 첫 생산분 · 레시피대로 직접 만든 시제품.
 * 실제 맛 예측 모델의 '시식 6건'은 공장 제품(샘플·첫 생산분)만 센다. 직접 만든 시식은 따로 보관한다 (대표 결정 2026-10-24).
 * 그 모델은 아직 없어 '6건' 기준값을 코드에 두지 않는다 — 모델을 만들 때 쓰는 곳과 함께 둔다 (1.0.6 점검 P2-37).
 */
export const TASTING_SOURCES = ['sample', 'first_production', 'self_made'] as const
export type TastingSource = (typeof TASTING_SOURCES)[number]

export function countsTowardTasteModel(source: TastingSource): boolean {
  return source !== 'self_made'
}

/** 항목별 1~5점: 맛·향·식감·전체 만족도 */
const score = z.number().int().min(1).max(5)
export const tastingScoresSchema = z.object({
  taste: score,
  smell: score,
  texture: score,
  overall: score,
})
export type TastingScores = z.infer<typeof tastingScoresSchema>
export const TASTING_SCORE_KEYS = ['taste', 'smell', 'texture', 'overall'] as const

export const createTastingSchema = z.object({
  simulationId: z.string().min(1).max(64),
  /** 공장을 고른 견적. 직접 만든 시식은 견적 없이도 남길 수 있다 */
  quoteId: z.string().min(1).max(64).nullable().default(null),
  /** 레시피캣 중계 샘플 주문 (AI 중계 샘플, 2026-10-30). 공장 샘플(sample)을 견적 대신 이 주문으로 남긴다. 옛 앱은 보내지 않는다 */
  sampleOrderId: z.string().min(1).max(64).nullable().optional(),
  source: z.enum(TASTING_SOURCES),
  scores: tastingScoresSchema,
  memo: z.string().trim().max(1000).default(''),
})
export type CreateTastingInput = z.infer<typeof createTastingSchema>

export const listTastingsQuerySchema = z.object({ simulationId: z.string().min(1).max(64) })

/**
 * GET /tastings/prediction. 추정하려면 레시피를 AI 제공자(Anthropic)에 다시 보낸다 — AI 전송 동의는 기기에만 있으니
 * 앱이 동의를 확인한 뒤 aiConsent=1 을 붙인다. 없으면 서버도 AI 를 부르지 않고 null (앱만 막으면 다른 호출이 샌다)
 */
export const tastePredictionQuerySchema = listTastingsQuerySchema.extend({
  aiConsent: z.literal('1').optional(),
})

export interface TastingView {
  id: string
  simulationId: string
  quoteId: string | null
  source: TastingSource
  /** 같은 제품·같은 출처의 몇 번째 시식인지 (1부터) */
  round: number
  scores: TastingScores
  memo: string
  /** 실제 맛 예측 모델의 시식 6건에 들어가는지 (공장 제품만) */
  countsTowardModel: boolean
  createdAt: string
  /** 이 시식을 남긴 샘플 주문 (AI 중계 샘플). 견적 시식·옛 기록은 null */
  sampleOrderId: string | null
}

/**
 * AI 맛 예측 (2026-10-25 대표 지시: 시식 데이터가 쌓이기 전에도 바로 보인다).
 * - recipe_estimate: 지금 생성에 쓰는 AI(Claude)가 레시피의 원료·배합·공정으로 추정한 점수. 시식 전 추정이라 카드에
 *   '추정' 꼬리표와 근거 한 줄을 늘 같이 보인다 (BR-13: 근거 없는 확정 점수처럼 보이면 안 된다)
 * - photo_model: 회사 사진 평가로 학습한 모델 (검증에 합격한 뒤). 학습 사진과의 거리(domain)를 같이 준다
 * 실제로 먹어 본 맛이 아니다. 시식 기록과 섞지 않고, 시식 화면이 둘을 나란히 비교한다 (공장 제품 시식이 보정 자료)
 */
export const TASTE_PREDICTION_BASES = ['recipe_estimate', 'photo_model'] as const
export type TastePredictionBasis = (typeof TASTE_PREDICTION_BASES)[number]

/**
 * interval80 은 이름만 80% 다 — 레시피 추정은 AI 가 적은 낮은~높은 점수라 통계 구간이 아니다.
 * 옛 앱이 이 칸 이름을 읽으므로 이름은 그대로 두고, 화면은 '예상 범위'로 적는다 (1.0.6 점검 P2-24)
 */
const expectation = z.object({
  prediction: z.number().min(1).max(5),
  interval80: z.tuple([z.number().min(1).max(5), z.number().min(1).max(5)]),
})
/** 근거 한 줄 — 앱 언어마다 (번역 호출을 따로 하지 않게 추정할 때 네 언어로 받는다) */
export const TASTE_RATIONALE_MAX = 160
const rationaleLine = z.string().trim().min(1).max(TASTE_RATIONALE_MAX)
const rationale = z.object({
  ko: rationaleLine,
  en: rationaleLine,
  ja: rationaleLine,
  zh: rationaleLine,
})

export const tastePredictionSchema = z.object({
  basis: z.enum(TASTE_PREDICTION_BASES),
  /** id = OpenRouter 모델 id 또는 사진 모델 이름 · version = 추정 방식 판 (프롬프트·모델을 바꾸면 올린다) */
  model: z.object({ id: z.string().min(1).max(80), version: z.string().min(1).max(40) }),
  taste: expectation,
  smell: expectation,
  texture: expectation,
  /** 먹고 싶은 정도 1~5 */
  desire: z.number().min(1).max(5),
  /** 가상 소비자 반응 7단계 비율 (매우 나쁨 → 매우 좋음, 합 1). 서버가 점수와 예상 범위로 시뮬레이션한다 */
  panel: z.array(z.number().min(0).max(1)).length(7),
  rationale,
  /** photo_model 만: 학습한 사진과의 거리. inside 가 아니면 화면이 점수를 판단 근거로 쓰지 말라고 알린다. 레시피 추정은 null */
  domain: z.enum(['inside', 'borderline', 'outside', 'near_duplicate']).nullable(),
})
export type TastePrediction = z.infer<typeof tastePredictionSchema>

/**
 * 근거 한 줄이 길면 버리지 않고 줄임표로 자른다. 프롬프트는 120자를 부탁하지만 모델이 글자 수를 넘기는 일이 있고,
 * 그때 답 전체를 버리면 카드가 1시간 동안 안 보였다 (2026-10-26). 서로게이트 쌍은 자르지 않는다
 */
export function clipRationaleLine(line: string, max = TASTE_RATIONALE_MAX): string {
  if (line.length <= max) return line
  let out = ''
  for (const ch of line) {
    if (out.length + ch.length > max - 1) break
    out += ch
  }
  return `${out.trimEnd()}…`
}
const estimateRationaleLine = z
  .string()
  .trim()
  .min(1)
  .transform((s) => clipRationaleLine(s))

/** 레시피 추정 AI 의 답 (pipeline/taste-estimate.ts). 점수 1~5 와 예상 범위(low~high), 먹고 싶은 정도, 네 언어 근거 */
const estimateScore = z
  .object({
    score: z.number().min(1).max(5),
    low: z.number().min(1).max(5),
    high: z.number().min(1).max(5),
  })
  .refine((s) => s.low <= s.score && s.score <= s.high, { message: 'low <= score <= high' })
export const tasteEstimateOutputSchema = z.object({
  taste: estimateScore,
  smell: estimateScore,
  texture: estimateScore,
  desire: z.number().min(1).max(5),
  rationale: z.object({
    ko: estimateRationaleLine,
    en: estimateRationaleLine,
    ja: estimateRationaleLine,
    zh: estimateRationaleLine,
  }),
})
export type TasteEstimateOutput = z.infer<typeof tasteEstimateOutputSchema>

/**
 * GET /tastings/prediction 이 prediction 이 null 인 까닭 (2026-10-26~ 서버). 앱은 이걸 보고 카드 자리를 정한다:
 * - ready: prediction 이 있다
 * - consent_required: 요청에 AI 전송 동의 표시(aiConsent=1)가 없다 — 앱은 동의를 묻는 카드를 그린다
 * - pending: 다른 요청이 추정하는 중이라 기다려도 끝나지 않았다 — 앱은 몇 초 뒤 다시 묻는다
 * - not_ready: 결과가 아직 완성(done)되지 않았다 — 앱은 몇 초 뒤 다시 묻는다
 * - disabled: 관리자가 settings.tastePrediction 을 껐다
 * - failed: 추정이 실패했다(잠깐 다시 부르지 않는다, kv-flags TASTE_FAIL_TTL_S)
 * 옛 서버(1.0.6 까지 배포분)는 state 칸 없이 { prediction } 만 준다. 앱은 칸이 없거나 모르는 값이면 예전처럼 prediction 만 본다
 */
export const TASTE_PREDICTION_STATES = [
  'ready',
  'consent_required',
  'pending',
  'not_ready',
  'disabled',
  'failed',
] as const
export type TastePredictionState = (typeof TASTE_PREDICTION_STATES)[number]

/** 앱이 읽는 응답 모양. 예측 모양이 틀리면 응답 전체가 아니라 prediction 만 null 로 본다 (카드만 빠지고 상태는 산다) */
export const tastePredictionResponseSchema = z.object({
  prediction: tastePredictionSchema.nullable().catch(null),
  state: z.enum(TASTE_PREDICTION_STATES).optional().catch(undefined),
})
export type TastePredictionResponse = z.infer<typeof tastePredictionResponseSchema>
